(() => {
  'use strict';
  if (window.__pplNeedsSpeciesTabs) return;
  window.__pplNeedsSpeciesTabs = true;
  const initialized = new WeakSet();
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  // Keep keyboard focus on a usable arrow when the focused one is about to be disabled.
  function setArrows(previous, next, previousOff, nextOff) {
    if (!previousOff) previous.disabled = false;
    if (!nextOff) next.disabled = false;
    const active = document.activeElement;
    if (nextOff && active === next && !previousOff) previous.focus({ preventScroll: true });
    if (previousOff && active === previous && !nextOff) next.focus({ preventScroll: true });
    previous.disabled = previousOff;
    next.disabled = nextOff;
  }

  function initialize(section) {
    if (initialized.has(section)) return;
    initialized.add(section);
    const tablist = section.querySelector('[data-needs-tablist]');
    const tabs = [...section.querySelectorAll('[data-needs-tab]')];
    const panels = [...section.querySelectorAll('[data-needs-panel]')];
    const cardSelector = section.dataset.tabsCard || '.need-card';
    const updates = new Map();
    const observers = [];
    panels.forEach(panel => {
      const rail = panel.querySelector('[data-needs-rail]');
      const controls = panel.querySelector('[data-needs-controls]');
      const previous = controls.querySelector('[data-needs-direction="-1"]');
      const next = controls.querySelector('[data-needs-direction="1"]');
      const position = controls.querySelector('[data-needs-position]');
      const cards = () => [...rail.querySelectorAll(cardSelector)];
      function metrics() {
        const items = cards();
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 22;
        const stride = items.length ? items[0].getBoundingClientRect().width + gap : 1;
        return { total: items.length, stride, visible: Math.max(1, Math.floor((rail.clientWidth + gap + 1) / stride)), start: Math.max(0, Math.round(rail.scrollLeft / stride)), scrollable: rail.scrollWidth > rail.clientWidth + 2 };
      }
      function update() {
        if (panel.hidden) return;
        const m = metrics();
        const start = Math.min(Math.max(0, m.total - 1), m.start);
        controls.hidden = !m.scrollable || m.total < 2;
        position.textContent = m.total ? `${start + 1}${m.visible > 1 ? '–' + Math.min(m.total, start + m.visible) : ''} ${position.dataset.needsOf || 'of'} ${m.total}${m.visible === 1 && position.dataset.needsSwipe ? ' · ' + position.dataset.needsSwipe : ''}` : '';
        setArrows(previous, next, !m.scrollable || rail.scrollLeft < 2, !m.scrollable || rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2);
      }
      function move(direction) {
        const m = metrics();
        rail.scrollBy({ left: direction * m.stride * m.visible, behavior: motion() });
      }
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      rail.addEventListener('scroll', update, { passive: true });
      rail.addEventListener('keydown', event => {
        if (event.target === rail && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          move(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      if (typeof ResizeObserver === 'function') {
        const observer = new ResizeObserver(update);
        observer.observe(rail);
        observers.push(observer);
      }
      updates.set(panel.dataset.needsPanel, update);
    });

    function select(tab, focus) {
      tabs.forEach(candidate => {
        const selected = candidate === tab;
        candidate.setAttribute('aria-selected', String(selected));
        candidate.tabIndex = selected ? 0 : -1;
      });
      panels.forEach(panel => {
        panel.hidden = panel.dataset.needsPanel !== tab.dataset.needsTab;
        if (!panel.hidden) panel.querySelector('[data-needs-rail]').scrollTo({ left: 0, behavior: 'instant' });
      });
      updates.get(tab.dataset.needsTab)?.();
      requestAnimationFrame(() => updates.get(tab.dataset.needsTab)?.());
      if (focus) tab.focus();
    }
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab, false));
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(index + tabs.length - 1) % tabs.length];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs[tabs.length - 1];
        if (next) { event.preventDefault(); select(next, true); }
      });
    });
    section.dataset.needsReady = 'true';
    tablist.hidden = false;
    select(tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0], false);
    section.addEventListener('shopify:block:select', event => {
      const card = event.target.closest('[data-ppl-need-card]');
      if (!card || !section.contains(card)) return;
      const tab = tabs.find(candidate => candidate.dataset.needsTab === card.dataset.needsSpecies);
      if (!tab) return;
      select(tab, false);
      const rail = card.closest('[data-needs-rail]');
      rail.scrollTo({ left: card.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft, behavior: 'instant' });
      updates.get(card.dataset.needsSpecies)?.();
    });
    section.addEventListener('ppl:needs-unload', () => observers.forEach(observer => observer.disconnect()), { once: true });
  }
  const scan = scope => {
    if (scope.matches?.('[data-ppl-needs-tabs]')) initialize(scope);
    scope.querySelectorAll('[data-ppl-needs-tabs]').forEach(initialize);
  };
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-ppl-needs-tabs]').forEach(section => section.dispatchEvent(new Event('ppl:needs-unload'))));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
